import { Injectable, inject, signal, effect, DestroyRef, OnDestroy } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from './api.service';
import { ConnectivityService } from './connectivity.service';
import { OfflineQueueService, QueuedAction } from './offline-queue.service';

/** Maximum number of retry attempts per queued action. */
const MAX_RETRIES = 3;

/** Base delay in milliseconds for exponential backoff. */
const BASE_DELAY_MS = 1000;

export interface SyncResult {
  total: number;
  succeeded: number;
  failed: number;
}

/**
 * SyncService synchronises queued offline actions to the backend when connectivity is restored.
 *
 * Responsibilities:
 * - Subscribe to ConnectivityService to detect when the device goes back online
 * - Process all queued actions in chronological order (ascending created_at)
 * - Map each action type to the appropriate backend endpoint
 * - Retry failed actions up to 3 times with exponential backoff (1s, 2s, 4s)
 * - Mark permanently failed actions with error and notify the resource
 *
 * Validates: Requirements 9.4, 9.5
 */
@Injectable({ providedIn: 'root' })
export class SyncService implements OnDestroy {
  private readonly api = inject(ApiService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly offlineQueue = inject(OfflineQueueService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  /** Whether a sync operation is currently in progress. */
  readonly isSyncing = signal(false);

  /** The most recent sync result (null if no sync has completed). */
  readonly lastSyncResult = signal<SyncResult | null>(null);

  /** Signal emitting failed action messages for UI notification. */
  readonly syncFailureMessage = signal<string | null>(null);

  /** Callback to refresh clock state after sync — set by AppComponent */
  onSyncComplete: (() => void) | null = null;

  private effectCleanup: (() => void) | null = null;
  private previousOnlineState = false;

  private onlineHandler: (() => void) | null = null;

  constructor() {
    // Track the initial state
    this.previousOnlineState = this.connectivity.isOnline();

    // Use effect to react to connectivity changes
    const effectRef = effect(() => {
      const isOnline = this.connectivity.isOnline();

      // Detect transition from offline → online
      if (isOnline && !this.previousOnlineState) {
        this.triggerSync();
      }

      this.previousOnlineState = isOnline;
    });

    // Also listen directly to the browser 'online' event as a backup
    // (the effect may not fire reliably on all platforms)
    this.onlineHandler = () => {
      console.log('[SyncService] Browser online event fired, triggering sync...');
      setTimeout(() => this.triggerSync(), 2000);
    };
    window.addEventListener('online', this.onlineHandler);
  }

  ngOnDestroy(): void {
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
    }
  }

  /**
   * Trigger synchronisation of all queued actions.
   * Can be called manually or is triggered automatically on reconnect.
   */
  async triggerSync(): Promise<SyncResult> {
    if (this.isSyncing()) {
      console.log('[SyncService] Already syncing, skipping.');
      return { total: 0, succeeded: 0, failed: 0 };
    }

    this.isSyncing.set(true);
    this.syncFailureMessage.set(null);

    try {
      const actions = await this.offlineQueue.getAllActions();
      console.log(`[SyncService] Found ${actions.length} queued actions to sync.`);

      if (actions.length === 0) {
        const result: SyncResult = { total: 0, succeeded: 0, failed: 0 };
        this.lastSyncResult.set(result);
        return result;
      }

      // Actions are already sorted by created_at (ascending) from the IndexedDB index
      let succeeded = 0;
      let failed = 0;

      for (const action of actions) {
        const success = await this.processAction(action);
        if (success) {
          succeeded++;
        } else {
          failed++;
        }
      }

      const result: SyncResult = { total: actions.length, succeeded, failed };
      this.lastSyncResult.set(result);

      if (failed > 0) {
        this.syncFailureMessage.set(
          this.translate.instant('sync.failed_message', { count: failed })
        );
      }

      // Notify listeners (e.g., refresh clock state from server)
      if (this.onSyncComplete) {
        this.onSyncComplete();
      }

      return result;
    } finally {
      this.isSyncing.set(false);
    }
  }

  /**
   * Process a single queued action: attempt to POST to the backend.
   * Retries up to MAX_RETRIES times with exponential backoff on failure.
   * Returns true if the action was successfully synced.
   */
  private async processAction(action: QueuedAction): Promise<boolean> {
    // If action has already been retried MAX_RETRIES times previously, mark as failed
    if (action.retry_count >= MAX_RETRIES) {
      return false;
    }

    let currentRetry = action.retry_count;

    while (currentRetry < MAX_RETRIES) {
      try {
        await this.sendToBackend(action);
        // Success — remove from queue
        await this.offlineQueue.deleteAction(action.id);
        return true;
      } catch (error: any) {
        currentRetry++;
        action.retry_count = currentRetry;
        action.last_error = error?.message || 'Unknown error';
        console.error(`[SyncService] Action ${action.type} failed (attempt ${currentRetry}):`, error?.message || error);
        await this.offlineQueue.updateAction(action);

        if (currentRetry < MAX_RETRIES) {
          // Wait with exponential backoff before retrying
          const delay = BASE_DELAY_MS * Math.pow(2, currentRetry - 1);
          await this.wait(delay);
        }
      }
    }

    // All retries exhausted — action stays in queue marked with error
    return false;
  }

  /**
   * Send a queued action to the appropriate backend endpoint based on its type.
   * Includes X-Offline-Sync header to prevent the auth interceptor from
   * triggering logout on 401 (token may have expired while offline).
   */
  private async sendToBackend(action: QueuedAction): Promise<void> {
    const path = this.getEndpointPath(action);
    const body = this.buildRequestBody(action);
    const headers = new HttpHeaders({ 'X-Offline-Sync': '1' });

    console.log(`[SyncService] Sending ${action.type} to ${path}`);

    const response = await firstValueFrom(
      this.api.post<{ ok: boolean; error?: string }>(path, body, { headers })
    );

    console.log(`[SyncService] Response:`, response);

    if (!response.ok) {
      throw new Error(response.error || 'Server returned an error');
    }
  }

  /**
   * Determine the backend API endpoint for a given action type.
   */
  private getEndpointPath(action: QueuedAction): string {
    switch (action.type) {
      case 'CLOCK_IN':
      case 'CLOCK_OUT':
        return `/tenants/${action.tenant_id}/resources/${action.resource_id}/clock`;
      case 'JOB_START':
        return `/tenants/${action.tenant_id}/bookings/${action.booking_id}/start`;
      case 'JOB_END':
        return `/tenants/${action.tenant_id}/bookings/${action.booking_id}/end`;
    }
  }

  /**
   * Build the request body for a given action type.
   */
  private buildRequestBody(action: QueuedAction): object {
    switch (action.type) {
      case 'CLOCK_IN':
      case 'CLOCK_OUT':
        return {
          type: action.type,
          timestamp: action.timestamp,
          lat: action.lat,
          lng: action.lng,
          location_unavailable: action.location_unavailable,
          gps_denied: action.gps_denied,
        };
      case 'JOB_START':
        return {
          timestamp: action.timestamp,
          lat: action.lat,
          lng: action.lng,
          location_unavailable: action.location_unavailable,
          gps_denied: action.gps_denied,
        };
      case 'JOB_END':
        return {
          timestamp: action.timestamp,
          lat: action.lat,
          lng: action.lng,
          location_unavailable: action.location_unavailable,
          gps_denied: action.gps_denied,
          form_data: action.form_data,
          attachment_keys: action.attachment_keys,
        };
    }
  }

  /**
   * Wait for the specified duration in milliseconds.
   */
  private wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
